import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import './Abilities.css';

function Abilities() {
  const [abilities, setAbilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const lastAbilityElementRef = useRef();
  const scrollContainerRef = useRef();

  const loadAbilities = useCallback(async (pageNum, search = '', shouldReset = false) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/abilities?page=${pageNum}&limit=20&search=${search}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch abilities');
      }
      const responseData = await response.json();
      const { data } = responseData;
      
      setAbilities(prev => shouldReset ? data.abilities : [...prev, ...data.abilities]);
      setHasMore(data.pagination.hasNextPage);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadAbilities(1, '', true);
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loadAbilities]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setPage(1);
        loadAbilities(1, searchTerm, true);
        // Reset scroll position on search
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadAbilities]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '20px',
      threshold: 0.1,
    };

    const handleObserver = (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        loadAbilities(nextPage, searchTerm, false);
      }
    };

    const currentObserver = new IntersectionObserver(handleObserver, options);

    if (lastAbilityElementRef.current) {
      currentObserver.observe(lastAbilityElementRef.current);
    }

    return () => {
      if (lastAbilityElementRef.current) {
        currentObserver.unobserve(lastAbilityElementRef.current);
      }
    };
  }, [loading, hasMore, page, searchTerm, loadAbilities]);

  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="abilities-page-container">
      <h2>Pokémon Abilities</h2>
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search abilities by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div 
        ref={scrollContainerRef}
        className="abilities-content-container"
      >
        <div className="abilities-grid">
          {abilities.map((ability, index) => (
            <Link 
              ref={index === abilities.length - 1 ? lastAbilityElementRef : null}
              to={`/abilities/${ability.name}`} 
              key={ability.name} 
              className="ability-card"
            >
              <h3>{ability.name}</h3>
              <p className="ability-effect">{ability.effect}</p>
            </Link>
          ))}
        </div>
        {loading && <div className="loading">Loading abilities...</div>}
      </div>
    </div>
  );
}

export default Abilities; 